import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { calendarIcon, chevronBackIcon, chevronForwardIcon, chevronForwardCircleIcon, closeCircleIcon, timeIcon } from '../icons';
import FlowFormLine from './FlowFormLine';
import React, { useEffect, useId, useMemo, useRef } from 'react';
import ImageButton from '../widgets/ImageButton';
import { setupPicker, destroyPicker, pickerSetOnSelection, pickerSelect } from '../widgets/PickerRenderer';
import { registerKeyHandler, unregisterKeyHandler } from '../structure/GlobalKeyHandler';
import { findBest } from '../helpers/utils';
import FocusText from '../widgets/FocusText';
import { calendarShowDateRelPosition, destroyCalendar, disableFocus, enableFocus, focusMoveVertical, focusMoveHorizontal, focusMoveMonth, getFocussedDate, setupCalendar, calendarSelect, calendarShowDatePosition, isVisible } from '../widgets/CalendarRenderer';
import { simpleDateTimeExtraInfo, parsePartialDateText, parseTimeText, timeEqual } from "../types/simpledate-tools";
import TextButton from '../widgets/TextButton';
import { focusElement, getKeyHandler } from '../structure/FlowForm';
import { createPickerKeyHandler } from '../widgets/PickerKeyHandler';
import { errorWobble } from '../helpers/wobble';
import '../common.css';
import './DateTimeLine.css';
import { dateEqual, dateGEQ, dateGreater, dateLEQ, dateLess, mergeDate, Months, MonthsShort, valiDate, Weekdays } from '../types/simpledate';
const pickerHours = Array.from({ length: 12 }, (_, i) => [(i + 1).toString(), (i + 1).toString()]);
const pickerMinutes = Array.from({ length: 60 }, (_, i) => [i.toString(), ('0' + i).slice(-2)]);
const pickerAmPm = [['am', 'am'], ['pm', 'pm']];
const pickerMonths = Array.from({ length: 12 }, (_, i) => [(i + 1).toString(), Months[i]]);
function getDayFromDate(year, month, date) {
    return new Date(year, month - 1, date).getDay();
}
function renderTime({ hour, minute }) {
    const hour12 = hour > 12 ? hour - 12 : hour;
    return `${hour12 || 12}:${('00' + minute).slice(-2)} ${hour >= 12 ? 'pm' : 'am'}`;
}
function renderDate({ year, month, date }) {
    return `${month}/${date}/${year}`;
}
function generateMonthRange(earliest, latest, currentDate) {
    const months = [];
    // Calculate the actual range, ensuring current date is included
    const actualEarliest = dateLess(earliest, currentDate) ? earliest : currentDate;
    const actualLatest = dateGreater(latest, currentDate) ? latest : currentDate;
    let rangeStart = actualEarliest;
    let rangeEnd = actualLatest;
    // Calculate total months in the range
    const totalMonths = (rangeEnd.year - rangeStart.year) * 12 + (rangeEnd.month - rangeStart.month) + 1;
    // If the range is more than 36 months, limit it but keep current date
    if (totalMonths > 36) {
        // Try to center around current date, but respect earliest/latest bounds
        const currentTotalMonths = (currentDate.year - rangeStart.year) * 12 + (currentDate.month - rangeStart.month);
        const monthsBefore = Math.min(18, currentTotalMonths);
        const monthsAfter = Math.min(18, totalMonths - currentTotalMonths - 1);
        // Adjust to use exactly 36 months
        const actualMonthsBefore = Math.min(monthsBefore, 36 - monthsAfter - 1);
        const actualMonthsAfter = Math.min(monthsAfter, 36 - actualMonthsBefore - 1);
        // Calculate new range
        let newStartYear = currentDate.year;
        let newStartMonth = currentDate.month - actualMonthsBefore;
        while (newStartMonth <= 0) {
            newStartMonth += 12;
            newStartYear--;
        }
        rangeStart = { year: newStartYear, month: newStartMonth, date: 1 };
        let newEndYear = currentDate.year;
        let newEndMonth = currentDate.month + actualMonthsAfter;
        while (newEndMonth > 12) {
            newEndMonth -= 12;
            newEndYear++;
        }
        rangeEnd = { year: newEndYear, month: newEndMonth, date: 1 };
    }
    let currentYear = rangeStart.year;
    let currentMonth = rangeStart.month;
    while (currentYear < rangeEnd.year || (currentYear === rangeEnd.year && currentMonth <= rangeEnd.month)) {
        const isFirstMonthOfYear = currentMonth === 1;
        const isFirstInRange = currentYear === rangeStart.year && currentMonth === rangeStart.month;
        let label = MonthsShort[currentMonth - 1];
        if (isFirstMonthOfYear || isFirstInRange) {
            label += ` '${currentYear.toString().slice(-2)}`;
        }
        months.push({
            year: currentYear,
            month: currentMonth,
            label
        });
        currentMonth++;
        if (currentMonth > 12) {
            currentMonth = 1;
            currentYear++;
        }
    }
    return months;
}
const DateTimeLine = ({ label, mode, value, date, time, onDateChange, earliestDate, latestDate, onTimeChange, onChange, optional, timeStep = 5, externalDrawerControl, onDrawerToggle, canOpen, actionButtons, disabled = false }) => {
    const now = new Date();
    const id = useId();
    const ref = React.useRef(null);
    const drawerRef = React.useRef(null);
    const dateRef = React.useRef(null);
    const timeRef = React.useRef(null);
    const monthNavRef = useRef(null);
    const [internalDrawerOpen, setInternalDrawerOpen] = React.useState(mode == 'time' ? 'closedTime' : 'closedDate');
    // Use external control if provided, otherwise use internal state
    const open = internalDrawerOpen;
    const setOpen = (newState) => {
        const resolvedState = typeof newState === 'function' ? newState(open) : newState;
        if (externalDrawerControl && onDrawerToggle) {
            const wasOpen = !open.startsWith('closed');
            const willBeOpen = !resolvedState.startsWith('closed');
            // Only allow opening if we notify the parent first
            if (!wasOpen && willBeOpen) {
                onDrawerToggle(true);
            }
        }
        setInternalDrawerOpen(resolvedState);
    };
    // Handle value prop - override date/time properties when value is provided
    const effectiveDate = date || (value ? { year: value.year, month: value.month, date: value.date } : undefined);
    const effectiveTime = time || (value ? { hour: value.hour, minute: value.minute } : undefined);
    const [monthPicker, setMonthPicker] = React.useState(null);
    const [yearPicker, setYearPicker] = React.useState(null);
    const [hourPicker, setHourPicker] = React.useState(null);
    const [minutePicker, setMinutePicker] = React.useState(null);
    const [amPmPicker, setAmPmPicker] = React.useState(null);
    // date and time are now controlled props
    const [dateInput, setDateInput] = React.useState(effectiveDate ? renderDate(effectiveDate) : '');
    const [dateInputOnEnter, setDateInputOnEnter] = React.useState('');
    const [timeInput, setTimeInput] = React.useState(effectiveTime ? renderTime(effectiveTime) : '');
    const [timeInputOnEnter, setTimeInputOnEnter] = React.useState('');
    const [currentTime, setCurrentTime] = React.useState({ hour: now.getHours(), minute: now.getMinutes() });
    const [currentDate, setCurrentDate] = React.useState({ year: now.getFullYear(), month: now.getMonth() + 1, date: now.getDate() });
    const [calendarRenderer, setCalendarRenderer] = React.useState(null);
    // Initialize displayDate to show appropriate month based on constraints
    const initialDisplayDate = React.useMemo(() => {
        if (effectiveDate)
            return effectiveDate;
        const current = { year: now.getFullYear(), month: now.getMonth() + 1, date: now.getDate() };
        const earliest = earliestDate !== null && earliestDate !== void 0 ? earliestDate : { year: now.getFullYear(), month: 1, date: 1 };
        const latest = latestDate !== null && latestDate !== void 0 ? latestDate : { year: now.getFullYear() + 3, month: 12, date: 31 };
        // If current date is within range, use it
        if (dateLEQ(earliest, current) && dateGEQ(latest, current)) {
            return current;
        }
        // If current date is before earliest, show earliest date's month
        if (dateGreater(earliest, current)) {
            return earliest;
        }
        // If current date is after latest, show latest date's month
        if (dateLess(latest, current)) {
            return latest;
        }
        return current;
    }, [date, earliestDate, latestDate, now]);
    const [displayDate, setDisplayDate] = React.useState(initialDisplayDate);
    const [error, setError] = React.useState('');
    // Handle external drawer control - close this drawer when canOpen is false
    useEffect(() => {
        if (externalDrawerControl && canOpen == false) {
            setInternalDrawerOpen(prev => prev.startsWith('closed') ? prev : (prev === 'time' ? 'closedTime' : 'closedDate'));
        }
    }, [externalDrawerControl, canOpen]);
    // Sync input fields when external date/time props change and close drawer
    const d = effectiveDate ? renderDate(effectiveDate) : '';
    useEffect(() => {
        setDateInput(d);
        setDateInputOnEnter(d);
        if (effectiveDate)
            setDisplayDate(effectiveDate);
        // Close drawer when external date changes
        setInternalDrawerOpen(prev => prev.startsWith('closed') ? prev : (prev === 'time' ? 'closedTime' : 'closedDate'));
    }, [d]);
    // Update displayDate when constraints change (if current displayDate is no longer appropriate)
    useEffect(() => {
        if (!date) {
            setDisplayDate(initialDisplayDate);
        }
    }, [earliestDate, latestDate]); // only earliestDate/latestDate to avoid infinite loop
    const t = effectiveTime ? renderTime(effectiveTime) : '';
    useEffect(() => {
        setTimeInput(t);
        setTimeInputOnEnter(t);
        // Close drawer when external time changes
        setPickerTime(effectiveTime);
        //setInternalDrawerOpen(prev => prev.startsWith('closed') ? prev : (prev === 'time' ? 'closedTime' : 'closedDate'));
    }, [t]);
    const drawerOpen = open != 'closedTime' && open != 'closedDate';
    const earliest = earliestDate !== null && earliestDate !== void 0 ? earliestDate : { year: now.getFullYear(), month: 1, date: 1 };
    const latest = latestDate !== null && latestDate !== void 0 ? latestDate : { year: now.getFullYear() + 3, month: 12, date: 31 };
    const isInRange = (d) => dateLEQ(earliest, d) && dateGEQ(latest, d);
    // Generate month range for navigation
    const monthRange = useMemo(() => generateMonthRange(earliest, latest, currentDate), [earliest, latest, currentDate]);
    // Find current month index for scrolling
    const currentMonthIndex = useMemo(() => {
        const displayMonth = displayDate;
        return monthRange.findIndex(m => m.year === displayMonth.year && m.month === displayMonth.month);
    }, [monthRange, displayDate]);
    // Scroll to current month when month navigation is first shown
    useEffect(() => {
        if (open === 'date' && currentMonthIndex >= 0 && monthNavRef.current) {
            const button = monthNavRef.current.children[currentMonthIndex];
            if (button) {
                button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    }, [open, currentMonthIndex]);
    function navigateToMonth(year, month) {
        const newDisplayDate = { year, month, date: 1 };
        setDisplayDate(newDisplayDate);
        // Position the calendar so the clicked month is the leftmost month displayed
        calendarRenderer && calendarShowDatePosition(calendarRenderer, newDisplayDate, true);
    }
    // update current time every minute
    useEffect(() => {
        const interval = setInterval(() => {
            const now = new Date();
            setCurrentTime({ hour: now.getHours(), minute: now.getMinutes() });
            setCurrentDate({ year: now.getFullYear(), month: now.getMonth() + 1, date: now.getDate() });
        }, 60000);
        return () => clearInterval(interval);
    }, []);
    const weekDay = useMemo(() => effectiveDate && Weekdays[getDayFromDate(effectiveDate.year, effectiveDate.month, effectiveDate.date)], [effectiveDate]);
    const shortYear = effectiveDate ? effectiveDate.year.toString() : '';
    const when = useMemo(() => effectiveDate ? simpleDateTimeExtraInfo(currentDate, currentTime, effectiveDate, effectiveTime) : '', [effectiveDate, effectiveTime, dateInput, timeInput]);
    function refocus() {
        var _a, _b;
        (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.querySelector('span.focusText')) === null || _b === void 0 ? void 0 : _b.focus();
    }
    function dateChanged(newDate) {
        const v = newDate ? renderDate(newDate) : '';
        setDateInput(v);
        setDateInputOnEnter(v);
        if (dateEqual(effectiveDate, newDate))
            return;
        setError('');
        calendarRenderer && calendarSelect(calendarRenderer, newDate);
        onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(newDate);
        onChange === null || onChange === void 0 ? void 0 : onChange(newDate && effectiveTime ?
            { year: newDate.year, month: newDate.month, date: newDate.date, hour: effectiveTime.hour, minute: effectiveTime.minute } :
            undefined);
    }
    function setPickerTime(newTime) {
        if (newTime && open == 'time' && hourPicker && minutePicker && amPmPicker) {
            pickerSelect(hourPicker, newTime ? ((newTime.hour % 12) || 12).toString() : '12');
            pickerSelect(minutePicker, newTime ? newTime.minute.toString() : '0');
            pickerSelect(amPmPicker, newTime && newTime.hour >= 12 ? 'pm' : 'am');
        }
    }
    function timeChanged(newTime) {
        const v = newTime ? renderTime(newTime) : '';
        setTimeInput(v);
        setTimeInputOnEnter(v);
        setError('');
        if (effectiveTime && timeEqual(effectiveTime, newTime))
            return;
        onTimeChange === null || onTimeChange === void 0 ? void 0 : onTimeChange(newTime);
        setPickerTime(newTime);
        onChange === null || onChange === void 0 ? void 0 : onChange(newTime && effectiveDate ?
            { year: effectiveDate.year, month: effectiveDate.month, date: effectiveDate.date, hour: newTime.hour, minute: newTime.minute } :
            undefined);
    }
    useEffect(() => {
        var _a, _b;
        if (!drawerRef.current)
            return;
        const calendarContainer = drawerRef.current.querySelector('.dateDrawer');
        const hourColumnRef = drawerRef.current.querySelector('.hourPickerColumn');
        const minuteColumnRef = drawerRef.current.querySelector('.minutePickerColumn');
        const amPmColumnRef = drawerRef.current.querySelector('.amPmPickerColumn');
        const monthColumnRef = drawerRef.current.querySelector('.monthPickerColumn');
        const yearColumnRef = drawerRef.current.querySelector('.yearPickerColumn');
        function destroyCalendarStuff() {
            calendarContainer && destroyCalendar(calendarContainer);
            setCalendarRenderer(undefined);
        }
        if (open == 'date' && calendarContainer)
            setCalendarRenderer(setupCalendar(calendarContainer, { onSelection: dateChanged, selected: effectiveDate, view: displayDate, earliest, latest, onDisplayMove: setDisplayDate }));
        else
            destroyCalendarStuff();
        function destroyTimeStuff() {
            setHourPicker(null);
            setMinutePicker(null);
            setAmPmPicker(null);
            if (!drawerRef.current)
                return;
            hourColumnRef && destroyPicker(hourColumnRef);
            minuteColumnRef && destroyPicker(minuteColumnRef);
            amPmColumnRef && destroyPicker(amPmColumnRef);
        }
        if (open == 'time' && hourColumnRef && minuteColumnRef && amPmColumnRef) {
            if (!effectiveTime) {
                const newTime = { hour: 9, minute: 0 };
                onTimeChange === null || onTimeChange === void 0 ? void 0 : onTimeChange(newTime);
                const r = renderTime(newTime);
                setTimeInput(r);
                setTimeInputOnEnter(r);
            }
            const pickerMinuteVals = timeStep == 1 ? pickerMinutes : pickerMinutes.filter((_, i) => i % timeStep == 0);
            const timeMinute = (_a = effectiveTime === null || effectiveTime === void 0 ? void 0 : effectiveTime.minute) !== null && _a !== void 0 ? _a : 0;
            const pickerMinuteSelection = (_b = findBest(pickerMinuteVals.map(v => parseInt(v[0])), (a, b) => Math.abs(a - timeMinute) <= Math.abs(b - timeMinute))) !== null && _b !== void 0 ? _b : 0;
            setHourPicker(setupPicker(hourColumnRef, pickerHours, undefined, effectiveTime ? (effectiveTime ? effectiveTime.hour % 12 : 12).toString() : "9", true, 'right'));
            setMinutePicker(setupPicker(minuteColumnRef, pickerMinuteVals, undefined, pickerMinuteSelection.toString(), timeStep < 10, 'center'));
            setAmPmPicker(setupPicker(amPmColumnRef, pickerAmPm, undefined, effectiveTime && (effectiveTime === null || effectiveTime === void 0 ? void 0 : effectiveTime.hour) >= 12 ? 'pm' : 'am', false, 'left'));
        }
        else
            destroyTimeStuff();
        function destroyMonthStuff() {
            if (!drawerRef.current)
                return;
            monthColumnRef && destroyPicker(monthColumnRef);
            yearColumnRef && destroyPicker(yearColumnRef);
        }
        if (open == 'month' && monthColumnRef && yearColumnRef) {
            const yearCount = latest.year - earliest.year + 1;
            const pickerYears = Array.from({ length: yearCount }, (_, i) => [(earliest.year + i).toString(), (earliest.year + i).toString()]);
            setMonthPicker(setupPicker(monthColumnRef, pickerMonths, (mstr) => { const month = parseInt(mstr); setDisplayDate(o => o && ({ ...o, month })); }, displayDate.month.toString(), true, 'right'));
            setYearPicker(setupPicker(yearColumnRef, pickerYears, (y) => { const year = parseInt(y); setDisplayDate(o => o && ({ ...o, year })); }, displayDate.year.toString(), false, 'left'));
        }
        else
            destroyMonthStuff();
        return () => {
            destroyCalendarStuff();
            destroyTimeStuff();
            destroyMonthStuff();
        };
    }, [open, ref.current, mode]);
    useEffect(() => {
        if (drawerOpen) {
            registerKeyHandler('datetime-' + id, {
                'Escape': () => {
                    if (open.startsWith('closed'))
                        return false;
                    close();
                    refocus();
                    return true;
                },
            }, false, 500);
        }
        else
            unregisterKeyHandler('datetime-' + id);
        return () => {
            unregisterKeyHandler('datetime-' + id);
        };
    }, [drawerOpen]);
    function toggleTimeDateDisplay(what, showSelection = false) {
        var _a;
        if (drawerOpen) {
            if (showSelection && what == 'date' && open == 'date' && calendarRenderer && effectiveDate && !isVisible(calendarRenderer, effectiveDate))
                return calendarShowDatePosition(calendarRenderer, effectiveDate);
            else if (what == open) {
                if (what == 'time')
                    (_a = timeRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                return close();
            }
        }
        setOpen(what);
        if (showSelection && calendarRenderer && what == 'date' && effectiveDate)
            calendarShowDatePosition(calendarRenderer, effectiveDate);
    }
    function toggleLastDisplay() {
        setOpen((o) => o == 'time' ? 'closedTime' : (o == 'date' || o == 'month' ? 'closedDate' : o == 'closedTime' ? 'time' : 'date'));
    }
    function close() {
        setOpen(o => o.startsWith('closed') ? o : (o == 'time' ? 'closedTime' : 'closedDate'));
    }
    function evalDate(value, leaving = false) {
        const parseAttempt = parsePartialDateText(value);
        if (!parseAttempt) {
            setError(leaving ? 'Invalid date' : '');
            return undefined;
        }
        const d = mergeDate(parseAttempt, effectiveDate !== null && effectiveDate !== void 0 ? effectiveDate : currentDate);
        if (effectiveDate && dateEqual(d, effectiveDate)) {
            setError('');
            return effectiveDate;
        }
        if (!valiDate(d)) {
            setError(leaving ? 'Invalid date' : '');
            return undefined;
        }
        if (dateGreater(earliest, d)) {
            setError('Must not be earlier than ' + renderDate(earliest));
            return undefined;
        }
        if (dateLess(latest, d)) {
            setError('Must not be later than ' + renderDate(latest));
            return undefined;
        }
        setError('');
        return d;
    }
    function onDateInput(ev) {
        const value = ev.target.value.replace(/^\s+/, '').toLowerCase();
        setDateInput(value);
        if (!/^(\d{1,2})[\/-]\d{1,2}[\/-](20\d\d|2[1-9]|3\d)$/.test(value))
            return;
        const fd = value ? evalDate(value) : undefined;
        if (!dateEqual(fd, effectiveDate)) {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(fd);
            calendarRenderer && calendarSelect(calendarRenderer, fd);
        }
    }
    function onDateInputBlur(ev) {
        const value = ev.target.value.replace(/^\s+/, '').toLowerCase();
        const fd = evalDate(value, true) || evalDate(value.replace(/^(\d{2}|[2-9])[\/-]*$/, '$1/').replace(/^(\d{1,2})[\/-]+(\d{2}|[4-9])[\/-]*/, '$1/$2/'), true);
        if (dateInput.trim() == '') {
            if (optional) {
                setError('');
                onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(undefined);
            }
            else if (timeInput.trim() == '' && mode == 'datetime')
                setError('Date and time required');
            else
                setError('Date required');
            return;
        }
        else if (dateRef.current && !fd)
            errorWobble(dateRef.current);
        if (fd)
            dateChanged(fd);
        else
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(undefined);
    }
    function evalTime(value, leaving = false) {
        const cleanValue = value.replace(/^\s+/, '').toLowerCase();
        const addedDigit = cleanValue.length > timeInput.length && cleanValue.slice(-1).match(/[0-9:apm.]/);
        const autoCompletedValue = addedDigit ? cleanValue.replace(/^(\d{2}|[3-9]):([ap])$/, '$1$2').replace(/^(\d{2}|[3-9]):*$/, '$1:').replace(/^(\d{1,2}):+(\d{2})\s*([ap])\.*[m]*(\.?)[\sapm.]*$/, '$1:$2 $3$4m$4') : cleanValue;
        setTimeInput(autoCompletedValue);
        const d = parseTimeText(autoCompletedValue);
        if (!d) {
            setError(leaving ? 'Invalid time' : '');
            return undefined;
        }
        setError('');
        return d;
    }
    function onTimeInput(ev) {
        setOpen(t => t == 'time' ? 'closedTime' : t);
        const d = evalTime(ev.target.value);
        if (!d)
            return;
    }
    function onTimeInputBlur(ev) {
        const val = ev.target.value;
        const fd = evalTime(val, true);
        if (val.trim() == '') {
            if (optional) {
                setError('');
                onTimeChange === null || onTimeChange === void 0 ? void 0 : onTimeChange(undefined);
            }
            else if (dateInput.trim() == '' && mode == 'datetime')
                setError('Date and time required');
            else
                setError('Time required');
        }
        else if (timeRef.current && !fd)
            errorWobble(timeRef.current);
        if (fd)
            timeChanged(fd);
        else
            onTimeChange === null || onTimeChange === void 0 ? void 0 : onTimeChange(undefined);
    }
    const escapeHandler = {
        'Escape': () => {
            close();
            return true;
        }
    };
    const handleDateKeyDown = getKeyHandler({
        sidewaysBehaviour: 'bothText',
        enterBehaviour: 'action',
        spaceBehaviour: 'action',
        onAction: (el) => {
            const str = el.value;
            const isFilled = !!str.trim();
            if (isFilled ? evalDate(str, true) : optional) {
                if (str == dateInputOnEnter && (effectiveDate || optional))
                    toggleTimeDateDisplay('date', true);
                else
                    focusElement(el, 1, false);
            }
            else
                errorWobble(el);
        },
        overrideHandlers: escapeHandler
    });
    const handleTimeKeyDown = getKeyHandler({
        sidewaysBehaviour: mode == 'time' ? 'rightAtEnd' : 'bothText',
        enterBehaviour: 'action',
        onAction: (el) => {
            const str = el.value;
            const isFilled = !!str.trim();
            if (isFilled ? evalTime(str, true) : optional) {
                if (str == timeInputOnEnter && (effectiveTime || optional))
                    toggleTimeDateDisplay('time');
                else
                    focusElement(el, 1, false);
            }
            else
                errorWobble(el);
        },
        overrideHandlers: escapeHandler,
    });
    hourPicker && pickerSetOnSelection(hourPicker, (hour) => { effectiveTime && timeChanged({ hour: parseInt(hour) % 12 + (effectiveTime.hour >= 12 ? 12 : 0), minute: effectiveTime.minute }); });
    minutePicker && pickerSetOnSelection(minutePicker, (minute) => { effectiveTime && timeChanged({ hour: effectiveTime.hour, minute: parseInt(minute) }); });
    amPmPicker && pickerSetOnSelection(amPmPicker, (amPm) => { effectiveTime && timeChanged({ hour: (effectiveTime.hour % 12 + (amPm == 'pm' ? 12 : 0)), minute: effectiveTime.minute }); });
    const monthPickerKeyDown = createPickerKeyHandler(() => { var _a, _b; setOpen('date'); (_b = (_a = drawerRef === null || drawerRef === void 0 ? void 0 : drawerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.monthYearButton')) === null || _b === void 0 ? void 0 : _b.focus(); }, monthPicker, () => { setOpen('date'); return true; });
    const yearPickerKeyDown = createPickerKeyHandler(() => { var _a, _b; setOpen('date'); (_b = (_a = drawerRef === null || drawerRef === void 0 ? void 0 : drawerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.monthYearButton')) === null || _b === void 0 ? void 0 : _b.focus(); }, yearPicker, () => { setOpen('date'); return true; });
    const hourPickerKeyDown = createPickerKeyHandler(() => toggleTimeDateDisplay('time'), hourPicker, () => { toggleTimeDateDisplay('time'); return true; });
    const minutePickerKeyDown = createPickerKeyHandler(() => toggleTimeDateDisplay('time'), minutePicker, () => { toggleTimeDateDisplay('time'); return true; });
    const amPmPickerKeyDown = createPickerKeyHandler(() => toggleTimeDateDisplay('time'), amPmPicker, () => { toggleTimeDateDisplay('time'); return true; });
    const calendarKeyOverrideMap = {
        'ArrowUp': () => {
            if (!calendarRenderer || !open)
                return false;
            if (focusMoveVertical(calendarRenderer, -1))
                return true;
            else {
                disableFocus(calendarRenderer);
                return false;
            }
        },
        'ArrowDown': () => {
            if (!calendarRenderer || !open)
                return false;
            if (focusMoveVertical(calendarRenderer, 1))
                return true;
            else {
                disableFocus(calendarRenderer);
                return false;
            }
        },
        'ArrowLeft': () => {
            calendarRenderer && focusMoveHorizontal(calendarRenderer, -1);
            return drawerOpen;
        },
        'ArrowRight': () => {
            calendarRenderer && focusMoveHorizontal(calendarRenderer, 1);
            return drawerOpen;
        },
        'PageDown': () => {
            calendarRenderer && focusMoveMonth(calendarRenderer, 1);
            return drawerOpen;
        },
        'PageUp': () => {
            calendarRenderer && focusMoveMonth(calendarRenderer, -1);
            return drawerOpen;
        },
        ...escapeHandler
    };
    const handleCalendarKeyDown = getKeyHandler({
        enterBehaviour: 'action',
        spaceBehaviour: 'action',
        sidewaysBehaviour: 'off',
        onAction: (el) => {
            var _a;
            const date = calendarRenderer && getFocussedDate(calendarRenderer);
            dateChanged(date);
            if (mode != 'datetime')
                focusElement(el, 1, false);
            else
                (_a = timeRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            close();
        },
        overrideHandlers: calendarKeyOverrideMap,
    });
    return _jsxs(_Fragment, { children: [_jsx(FlowFormLine, { type: drawerOpen ? 'unfolded' : 'primary', onClick: disabled ? undefined : toggleLastDisplay, disabled: disabled, children: _jsxs("div", { className: 'dateTimeLine', ref: ref, children: [_jsx("label", { children: label }), _jsxs("div", { className: 'errorContainer', children: [_jsxs("div", { className: 'content', children: [(mode != 'time') && _jsxs(_Fragment, { children: [_jsx(ImageButton, { mediaSelection: 'desktopOnly', icon: calendarIcon, focussable: 'sideway', size: 'medium', onClick: () => toggleTimeDateDisplay('date', true), disabled: disabled }), _jsx("input", { ref: dateRef, className: `desktopOnly dateInput flowFocussable`, type: 'text', value: dateInput, onFocus: (ev) => { const v = ev.target.value; setDateInputOnEnter(v); evalDate(v); if (drawerOpen)
                                                        setOpen('date'); }, onInput: onDateInput, onBlur: onDateInputBlur, onKeyDown: handleDateKeyDown, onClick: () => { if (!drawerOpen)
                                                        toggleTimeDateDisplay('date', true); }, placeholder: 'mm/dd/yyyy', size: 10, disabled: disabled })] }), mode != 'date' && _jsxs(_Fragment, { children: [_jsx(ImageButton, { mediaSelection: 'desktopOnly', icon: timeIcon, focussable: 'sideway', size: 'medium', onClick: () => toggleTimeDateDisplay('time'), disabled: disabled }), _jsx("input", { ref: timeRef, className: `desktopOnly timeInput flowFocussable ${mode == 'time' ? '' : 'sidewayFocus'}`, type: 'text', value: timeInput, onFocus: (ev) => { const v = ev.target.value; setTimeInputOnEnter(v); evalTime(v); if (drawerOpen)
                                                        setOpen('time'); }, onInput: onTimeInput, onBlur: onTimeInputBlur, onKeyDown: handleTimeKeyDown, onClick: () => { if (!drawerOpen)
                                                        toggleTimeDateDisplay('time', true); }, placeholder: 'hh:mm am', size: 9, disabled: disabled })] }), mode != 'time' && _jsx(FocusText, { className: `touchOnly dateText${effectiveDate ? '' : ' no-selection'}`, focussable: "flow", onClick: disabled ? undefined : () => toggleTimeDateDisplay('date', true), children: effectiveDate ? `${weekDay === null || weekDay === void 0 ? void 0 : weekDay.slice(0, 3)}, ${MonthsShort[effectiveDate.month - 1]} ${effectiveDate.date} ${shortYear}` : 'Select date' }), mode != 'date' && _jsx(FocusText, { className: `touchOnly timeText${effectiveTime ? '' : ' no-selection'}`, focussable: mode == 'time' ? 'flow' : 'sideway', onClick: disabled ? undefined : undefined, children: effectiveTime ? renderTime(effectiveTime) : 'Select time' }), optional && (timeInput || dateInput) && !disabled && _jsx(ImageButton, { icon: closeCircleIcon, className: 'clear', size: "medium", focussable: 'sideway', sidewaysBehaviour: 'both', onClick: () => { dateChanged(undefined); timeChanged(undefined); } }), actionButtons && !disabled && actionButtons.map((button, index) => (_jsx(ImageButton, { className: `half-margin-left ${button.className || ''}`, icon: button.icon, size: 'medium', focussable: 'sideway', onClick: button.onClick, title: button.title }, index))), when && _jsx("div", { className: 'desktopOnly when', children: when }), !disabled && _jsx(ImageButton, { icon: chevronForwardCircleIcon, size: 'medium', focussable: 'normal', sidewaysBehaviour: 'both', turn: open.startsWith('closed') ? 0 : 270, onClick: toggleLastDisplay }), optional && _jsx("span", { className: 'remark desktopOnly', children: "(optional)" })] }), error && _jsx("div", { className: "errorMessage", children: error })] })] }) }), _jsx("div", { className: `dateTimeLineDrawerContainer ${drawerOpen ? 'open' : 'closed'}`, ref: drawerRef, children: _jsxs("div", { className: `dateTimeLineDrawer`, children: [mode != 'time' && (open == 'date' || open == 'closedDate' || open == 'month') &&
                            _jsx(_Fragment, { children: _jsxs("div", { className: 'dateHeader', children: [_jsx(TextButton, { label: `${Months[displayDate.month - 1]} ${displayDate.year}`, className: 'monthYearButton', type: 'borderless', enabled: drawerOpen ? 'enabled' : 'disabled', sidewaysBehaviour: 'right', chevron: open == 'month' ? 'up' : 'right', onClick: () => { setOpen(open == 'date' ? 'month' : 'date'); } }), _jsx("div", { style: { flex: 1 } }), open == 'date' && drawerOpen && _jsxs(_Fragment, { children: [isInRange(currentDate) && _jsx(TextButton, { label: 'Today', type: 'minor', focussable: 'sideway', onClick: () => { dateChanged(currentDate); } }), _jsx(ImageButton, { icon: chevronBackIcon, size: 'medium', focussable: 'sideway', onClick: () => calendarRenderer && calendarShowDateRelPosition(calendarRenderer, -1) }), _jsx(ImageButton, { icon: chevronForwardIcon, size: 'medium', focussable: 'sideway', sidewaysBehaviour: 'left', onClick: () => calendarRenderer && calendarShowDateRelPosition(calendarRenderer, 1) })] })] }) }), mode != 'time' && (open == 'date' || open == 'closedDate') &&
                            _jsxs(_Fragment, { children: [_jsx("div", { className: `dateDrawer flowFocussable`, tabIndex: drawerOpen ? 0 : undefined, onFocus: () => calendarRenderer && enableFocus(calendarRenderer), onBlur: () => calendarRenderer && disableFocus(calendarRenderer), onKeyDown: handleCalendarKeyDown }), _jsx("div", { className: 'monthNavigation', ref: monthNavRef, children: monthRange.map((monthInfo) => (_jsx(TextButton, { label: monthInfo.label, type: monthInfo.year === displayDate.year && monthInfo.month === displayDate.month ? 'major' : 'minor', size: 'small', focussable: 'sideway', onClick: () => navigateToMonth(monthInfo.year, monthInfo.month) }, `${monthInfo.year}-${monthInfo.month}`))) })] }), open == 'month' && _jsxs("div", { className: 'multiPickerDrawer', children: [_jsx("div", { className: "monthPickerColumn pickerColumn flowFocussable", tabIndex: 0, onKeyDown: monthPickerKeyDown }), _jsx("div", { className: "yearPickerColumn pickerColumn flowFocussable sidewayFocus", tabIndex: 0, onKeyDown: yearPickerKeyDown })] }), mode != 'date' && (open == 'time' || open == 'closedTime') &&
                            _jsxs(_Fragment, { children: [_jsxs("div", { className: 'multiPickerDrawer', children: [_jsx("div", { className: "hourPickerColumn pickerColumn flowFocussable", tabIndex: open == 'time' ? 0 : undefined, onKeyDown: hourPickerKeyDown }), _jsx("div", { className: "minutePickerColumn pickerColumn flowFocussable sidewayFocus", tabIndex: open == 'time' ? 0 : undefined, onKeyDown: minutePickerKeyDown }), _jsx("div", { className: "amPmPickerColumn pickerColumn flowFocussable sidewayFocus", tabIndex: open == 'time' ? 0 : undefined, onKeyDown: amPmPickerKeyDown })] }), _jsxs("div", { className: 'timeShortcuts', children: [_jsx(TextButton, { label: 'Midnight', type: 'minor', size: 'small', focussable: 'flow', onClick: () => timeChanged({ hour: 0, minute: 0 }) }), _jsx(TextButton, { label: '8am', type: 'minor', size: 'small', focussable: 'sideway', onClick: () => timeChanged({ hour: 8, minute: 0 }) }), _jsx(TextButton, { label: '5pm', type: 'minor', size: 'small', focussable: 'sideway', onClick: () => timeChanged({ hour: 17, minute: 0 }) }), _jsx(TextButton, { label: 'end of day', type: 'minor', size: 'small', focussable: 'sideway', onClick: () => timeChanged({ hour: 23, minute: 55 }) })] })] })] }) })] });
};
export default DateTimeLine;
